import { useEffect, useMemo } from 'react'

import { useLoaderData, useLocation, useRouteLoaderData } from 'react-router'

import { ProductContentLoaderData, loader as contentLoader } from './loader'
import { A2AViewer } from '~/portal/components/A2AViewer'
import ApiViewer from '~/portal/components/ApiViewer'
import { ConsumerFooter } from '~/portal/components/ConsumerFooter'
import { GraphQlViewer } from '~/portal/components/GraphQlViewer'
import { GrpcViewer } from '~/portal/components/GrpcViewer'
import { MCPViewer } from '~/portal/components/MCPViewer'
import { SyntaxRenderer } from '~/portal/components/SyntaxRenderer'
import { DOCS_SCROLLABLE_CONTENT_ID } from '~/portal/components/SyntaxRenderer/hooks/useTableOfContents'
import { useConsumerBranding } from '~/portal/hooks/useConsumerBranding'
import { useProducts } from '~/portal/hooks/useProducts'
import { AsyncAPIDefinition, OpenAPI2Definition, OpenAPI3Definition } from '~/portal/types/api.definitions'
import { ConsumerLoaderData } from '~/portal/types/portal'
import {
  getProviderContentSlug,
  getProviderTemplateContentSlug,
  getTOCBreadcrumbPaths,
  isPublishedA2aContent,
  isPublishedApiContent,
  isPublishedDocumentContent,
  isPublishedGraphQlContent,
  isPublishedGrpcContent,
  isPublishedMcpContent,
} from '~/portal/utils/shared/content'
import { formatDate } from '~/portal/utils/shared/dateTime'
import { consumerMeta } from '~/portal/utils/shared/meta'
import { ErrorBoundaryComponent } from '~/shared/components/ErrorBoundary'
import { useShubOrgInfo } from '~/shared/hooks/useShubOrgInfo'
import { CONTENT_TYPE } from '~/shared/types/content'
import { isAtLeastDesigner } from '~/shared/utils/roles'

export const handle = {
  nestedBreadcrumbs: ({ match, matches }) => {
    const toc = matches.find(match => match.id === 'routes/_docs.$productname/_index')?.loaderData?.sections?.items?.[0]?.tableOfContents
    return getTOCBreadcrumbPaths(toc, match.pathname)
  }
}

export const loader = contentLoader

export const meta = consumerMeta

export default function DocsProductDocumentationRootPage() {
  const {
    currentToc,
    type,
    content,
    contentUpdatedAt,
    path,
    generatorLanguages,
    exploreUrl,
    shubUrl,
    SHUBApiDetails = {},
    apiRenderer,
    routingSettings,
    isTemplatePreview,
    isProductPreview,
    flattenAvailable,
  } = useLoaderData<ProductContentLoaderData>()

  const { accentColor, fontName } = useConsumerBranding()

  const { footer } = useRouteLoaderData<ConsumerLoaderData>('routes/_docs/_index') || {}

  const { currentProduct, currentProductRole } = useProducts()
  const showEditButtons = isAtLeastDesigner(currentProductRole)
  const { swaggerhubOrgName } = useShubOrgInfo()
  const lastModifiedDate = useMemo(() => formatDate(contentUpdatedAt), [contentUpdatedAt])
  const providerContentSlug = isTemplatePreview && currentToc && 'sourceTemplateTocId' in currentToc
    ? getProviderTemplateContentSlug({ templateId: currentProduct?.id, contentId: currentToc.sourceTemplateTocId })
    : getProviderContentSlug({ productSlug: currentProduct?.slug, contentId: currentToc?.tableOfContentsId })

  const location = useLocation()

  const isMarkdown = type === CONTENT_TYPE.MARKDOWN
  const isPreview = isTemplatePreview || isProductPreview

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(decodeURIComponent(location.hash.slice(1)))
      if (element) {
        element.scrollIntoView()
      }
    }
  }, [location])

  const renderApi = () => {
    if (type === CONTENT_TYPE.GRAPHQL) {
      return (
        <GraphQlViewer
          key={currentToc?.tableOfContentsId}
          tableOfContentsId={currentToc?.tableOfContentsId}
          title={currentToc?.title}
          registryUrl={isPublishedGraphQlContent(currentToc) ? currentToc.url : undefined}
          content={content}
          lastModifiedDate={lastModifiedDate}
          providerContentSlug={providerContentSlug}
          showEditButtons={showEditButtons}
          accentColor={accentColor}
          fontName={fontName}
          downloadDisabled={isPreview}
        />
      )
    }

    if (type === CONTENT_TYPE.MCP) {
      return (
        <MCPViewer
          key={currentToc?.tableOfContentsId}
          tableOfContentsId={currentToc?.tableOfContentsId}
          title={currentToc?.title}
          registryUrl={isPublishedMcpContent(currentToc) ? currentToc.url : undefined}
          content={content}
          lastModifiedDate={lastModifiedDate}
          providerContentSlug={providerContentSlug}
          shubUrl={shubUrl}
          showEditButtons={showEditButtons}
          accentColor={accentColor}
          fontName={fontName}
          downloadDisabled={isPreview}
        />
      )
    }

    if (type === CONTENT_TYPE.A2A) {
      return (
        <A2AViewer
          key={currentToc?.tableOfContentsId}
          tableOfContentsId={currentToc?.tableOfContentsId}
          title={currentToc?.title}
          registryUrl={isPublishedA2aContent(currentToc) ? currentToc.url : undefined}
          content={content}
          lastModifiedDate={lastModifiedDate}
          providerContentSlug={providerContentSlug}
          shubUrl={shubUrl}
          showEditButtons={showEditButtons}
          accentColor={accentColor}
          fontName={fontName}
          downloadDisabled={isPreview}
        />
      )
    }

    if (type === CONTENT_TYPE.GRPC) {
      return (
        <GrpcViewer
          key={currentToc?.tableOfContentsId}
          tableOfContentsId={currentToc?.tableOfContentsId}
          title={currentToc?.title}
          registryUrl={isPublishedGrpcContent(currentToc) ? currentToc.url : undefined}
          content={content}
          lastModifiedDate={lastModifiedDate}
          providerContentSlug={providerContentSlug}
          shubUrl={shubUrl}
          showEditButtons={showEditButtons}
          accentColor={accentColor}
          fontName={fontName}
          downloadDisabled={isPreview}
        />
      )
    }

    return <ApiViewer
      key={currentToc?.tableOfContentsId}
      title={currentToc?.title}
      tableOfContentsId={currentToc?.tableOfContentsId}
      definition={content as OpenAPI2Definition | OpenAPI3Definition | AsyncAPIDefinition}
      generatorLanguages={generatorLanguages}
      productId={currentProduct?.id}
      orgName={swaggerhubOrgName}
      path={path}
      exploreUrl={exploreUrl}
      shubUrl={shubUrl}
      SHUBApiDetails={SHUBApiDetails}
      showEditButtons={showEditButtons}
      renderer={apiRenderer}
      lastModifiedDate={lastModifiedDate}
      routingSettings={routingSettings}
      providerContentSlug={providerContentSlug}
      isConsumerSide={true}
      flattenAvailable={flattenAvailable}
      downloadDisabled={isPreview}
    />
    
  }

  const isApiTocItem = isPublishedApiContent(currentToc)

  return (
    <div id={DOCS_SCROLLABLE_CONTENT_ID} className="flex flex-col h-full overflow-auto overflow-x-hidden">
      {(type && type !== 'invalid') && (
        <>
          <div className="flex-1">
            {isPublishedDocumentContent(currentToc) ? (
              <div
                data-testid={`${isMarkdown ? 'markdown-container' : 'html-container'}`}
                className="relative h-full"
                style={{ fontFamily: fontName }}
              >
                <SyntaxRenderer
                  key={currentToc?.tableOfContentsId}
                  type={isMarkdown ? CONTENT_TYPE.MARKDOWN : CONTENT_TYPE.HTML}
                  content={content}
                  title={currentToc?.title}
                  lastModifiedDate={lastModifiedDate}
                  providerContentSlug={providerContentSlug}
                  showEditButtons={showEditButtons}
                  isConsumerSide={true}
                />
              </div>
            ) : null}
            {isApiTocItem ? renderApi() : null}
          </div>
          {footer?.visible && <ConsumerFooter footer={footer} />}
        </>
      )}
    </div>
  )
}

export const ErrorBoundary = ErrorBoundaryComponent